import React, { useState } from 'react'
import type { FC } from 'react'
import {
  useProjectMembers,
  useAddProjectMembers,
  useRemoveProjectMember
} from '../hooks/useProjectContacts'
import { useIronworkerPool } from '../hooks/useIronworkerPool'
import { X, Plus } from 'lucide-react'

interface ProjectMembersTabProps {
  projectId: string
}

const ProjectMembersTab: FC<ProjectMembersTabProps> = ({ projectId }) => {
  const membersQuery = useProjectMembers(projectId)
  const poolQuery = useIronworkerPool()
  const addMutation = useAddProjectMembers(projectId)
  const removeMutation = useRemoveProjectMember(projectId)
  const members = membersQuery.data ?? []
  const pool = poolQuery.data ?? []
  const [showAdd, setShowAdd] = useState(false)
  const [selection, setSelection] = useState<string[]>([])

  if (membersQuery.isLoading) return <div>Loading members...</div>
  if (membersQuery.error) return <div>Error loading members</div>

  const handleAdd = () => {
    if (selection.length) {
      addMutation.mutate(selection, {
        onSuccess: () => setShowAdd(false)
      })
    }
  }

  const handleRemove = (contactId: string) => {
    removeMutation.mutate(contactId)
  }

  // Exclude already assigned
  const available = pool.filter(c => !members.some(m => m._id === c._id))

  return (
    <>
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Project Members</h2>
          <button
            onClick={() => setShowAdd(true)}
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
          >
            <Plus className="mr-1 w-4 h-4" /> Add Members
          </button>
        </div>
        <div className="shadow border border-gray-200 rounded-lg overflow-x-auto mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map(member => (
                <tr key={member._id}>
                  <td className="px-4 py-2">{member.name}</td>
                  <td className="px-4 py-2">{member.email}</td>
                  <td className="px-4 py-2">{new Date(member.createdAt || '').toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleRemove(member._id)}
                      disabled={removeMutation.isPending}
                      className="text-red-600 hover:underline flex items-center disabled:opacity-50"
                    >
                      <X className="w-4 h-4 mr-1" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-center text-gray-500">
                    No members assigned.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {showAdd && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
              <div className="bg-white p-6 rounded shadow-lg w-96">
                <h3 className="text-lg font-medium mb-4">Add Ironworkers</h3>
                {poolQuery.isLoading ? (
                  <div>Loading...</div>
                ) : (
                  <div className="max-h-40 overflow-auto mb-4 p-2 border rounded bg-white text-gray-800">
                    {available.map(c => (
                      <label key={c._id} className="flex items-center space-x-2 mb-1">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          checked={selection.includes(c._id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelection(prev => [...prev, c._id])
                            } else {
                              setSelection(prev => prev.filter(id => id !== c._id))
                            }
                          }}
                        />
                        <span>{c.name} ({c.email})</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowAdd(false)}
                    className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAdd}
                    className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
                    disabled={addMutation.isPending || selection.length === 0}
                  >
                    Add Selected
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectMembersTab;
